import { useTranslation } from 'react-i18next';
import { Card } from '../ui/Card';
import type { ScoringResultWithId } from '../../types/api.js';

interface MarketIntelligenceProps {
  /** スコアリング結果 */
  result: ScoringResultWithId;
}

/**
 * @graph-stack mall
 * @graph-domain Operations
 * @graph-business マーケットインテリジェンス表示。ブランド情報・マッチタイプ・Amazon評価・mybest順位等の市場情報を集約表示
 * @graph-connects none
 */
export function MarketIntelligence({ result }: MarketIntelligenceProps) {
  const { t } = useTranslation();

  return (
    <Card variant="glass">
      <h3 className="text-sm font-semibold text-[var(--color-text-primary)] mb-3">
        {t('scoring.marketTitle')}
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* ブランド情報 */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-[var(--color-text-muted)]">{t('scoring.brand')}</span>
            <span className="text-[var(--color-text-primary)] font-medium">
              {result.brandLabel}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-[var(--color-text-muted)]">{t('scoring.category')}</span>
            <span className="text-[var(--color-text-primary)] font-medium">
              {result.categoryKey ?? '-'}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-[var(--color-text-muted)]">{t('scoring.matchType')}</span>
            <span className="text-[var(--color-text-secondary)] text-xs">{result.matchLabel}</span>
          </div>
        </div>

        {/* 外部データ */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-[var(--color-text-muted)]">{t('scoring.price')}</span>
            <span className="text-[var(--color-text-primary)] font-mono font-medium">
              {result.price.toLocaleString()}
            </span>
          </div>
          {result.amazonRating != null && (
            <div className="flex justify-between text-sm">
              <span className="text-[var(--color-text-muted)]">Amazon</span>
              <span className="text-[var(--color-text-primary)] font-medium">
                {'★'.repeat(Math.round(result.amazonRating))} {result.amazonRating.toFixed(1)}
              </span>
            </div>
          )}
          {result.mybestRank != null && (
            <div className="flex justify-between text-sm">
              <span className="text-[var(--color-text-muted)]">mybest</span>
              <span className="text-[var(--color-text-primary)] font-medium">
                #{result.mybestRank}
              </span>
            </div>
          )}
          <div className="flex justify-between text-sm">
            <span className="text-[var(--color-text-muted)]">{t('scoring.conversionRate')}</span>
            <span className="text-[var(--color-text-primary)] font-mono font-medium">
              {result.lineData.rate.toFixed(1)}%
            </span>
          </div>
        </div>
      </div>

      {/* 補足情報 */}
      {result.supplement && (
        <div className="mt-4 p-3 rounded-lg bg-[var(--color-warning-muted)] border border-[rgba(245,158,11,0.2)]">
          <p className="text-sm text-[var(--color-text-primary)]">{result.supplement}</p>
        </div>
      )}
    </Card>
  );
}
